/**
 * Base Query with Reauth - Global 401 interceptor for RTK Query APIs
 *
 * Wraps fetchBaseQuery to intercept 401 responses at the API layer before
 * they reach individual components. When a 401 is detected (e.g., after a
 * user is migrated from B2C to Kinde mid-session), clears the auth provider
 * cache and redirects to the sign-out page.
 *
 * Also provides `createAuthenticatedBaseQuery` which bundles the shared
 * `prepareHeaders` logic used by all authenticated APIs (api.js, quickPartsApi.js,
 * migrationApi.js).
 *
 * @module baseQueryWithReauth
 * @see docs/plans/2026-04-09-global-401-interceptor.md
 */

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { API_BASE_URL } from '@/common/config/api'
import { AUTH_PROVIDERS, getAuthProvider } from '@/common/helpers/auth/authProviderDetection'
import { getApiAccessToken } from '@/common/helpers/auth/getApiAccessToken'
import { SIGNOUT_PATHNAME } from '@/common/utils/Paths/Paths'

/**
 * Module-level flag to prevent multiple concurrent redirects.
 * When several RTK Query hooks fire simultaneously and all return 401,
 * only the first one should trigger the redirect.
 *
 * @type {boolean}
 */
let isRedirecting = false

/**
 * Reset the redirecting flag. Exported for test cleanup only.
 *
 * @private
 */
export const _resetRedirectingFlag = () => {
    isRedirecting = false
}

/**
 * Shared prepareHeaders function for all authenticated APIs.
 * Injects the Bearer token and optional Authorization-Scheme header.
 *
 * @param {Headers} headers - The request headers
 * @returns {Promise<Headers>} Headers with auth token injected
 */
export const prepareAuthHeaders = async (headers) => {
    const { token } = await getApiAccessToken()

    if (token) {
        headers.set('authorization', `Bearer ${token}`)

        // QM-3353: Always send the Authorization-Scheme hint when the provider
        // is Kinde, regardless of email-first flag state. The flag historically
        // gated this header during the migration window; post-migration the
        // backend still needs the hint to validate Kinde JWTs correctly.
        // Use an allow-list (=== KINDE) rather than a deny-list (!== MSAL) so
        // unrecognized cache values fail closed — the backend defaults to MSAL
        // validation when the header is absent, which is the safer default.
        if (getAuthProvider() === AUTH_PROVIDERS.KINDE) {
            headers.set('Authorization-Scheme', AUTH_PROVIDERS.KINDE)
        }
    }
    return headers
}

/**
 * Wraps fetchBaseQuery with 401 interception.
 *
 * On 401: purges all auth state (provider cache, MSAL/Kinde storage keys,
 * RTK Query cache, redux-persist) and always redirects to /signout.
 * The signout page verifies cleanup and provides a clean re-entry to /login.
 *
 * The error is still returned so pending promises resolve normally
 * while the redirect is in progress.
 *
 * @param {Object} baseQueryConfig - Configuration passed to fetchBaseQuery
 * @returns {Function} A baseQuery function compatible with RTK Query createApi
 */
export const createBaseQueryWithReauth = (baseQueryConfig) => {
    const rawBaseQuery = fetchBaseQuery(baseQueryConfig)

    return async (args, api, extraOptions) => {
        const result = await rawBaseQuery(args, api, extraOptions)

        if (result.error?.status === 401 && !isRedirecting) {
            isRedirecting = true
            // Dynamic import to avoid circular dependency:
            // purgeAuthState → store → api → baseQueryWithReauth → purgeAuthState
            const { purgeAuthState } = await import('@/common/helpers/auth/purgeAuthState')
            await purgeAuthState()
            console.warn('[baseQueryWithReauth] 401 received — redirecting to /signout')
            window.location.href = SIGNOUT_PATHNAME
        }

        return result
    }
}

/**
 * Creates an authenticated base query with 401 interception and shared headers.
 *
 * Convenience factory that bundles:
 * - fetchBaseQuery with API_BASE_URL and 30s timeout
 * - prepareAuthHeaders (Bearer token + Authorization-Scheme)
 * - 401 interception (clear cache + redirect to /signout)
 *
 * @param {Object} [config={}] - Optional overrides for fetchBaseQuery config
 * @returns {Function} A baseQuery function compatible with RTK Query createApi
 */
export const createAuthenticatedBaseQuery = (config = {}) =>
    createBaseQueryWithReauth({
        baseUrl: API_BASE_URL,
        timeout: 30000,
        prepareHeaders: prepareAuthHeaders,
        ...config,
    })
